import { useCallback, useEffect, useState } from "react";
import {
  gqlClient,
  gqlHeader,
  runGqlQueryV2,
} from "../../../../../../graphql/client";
import { toast } from "react-toastify";
import { reportUnexpectedError } from "@graffer-inc/smart-apply";
import { GetInvoiceBusinessIssuersByOrgIdQuery } from "../../../../../../graphql/generated/types";
import { Auth } from "../../../../../../application/domain/auth/auth";
import { useEnablePaymentHooks } from "./payment-settings";

type redirectToType = "new" | "edit";

type Props = {
  orgId: string;
  auth: Auth;
};

export const useQualifiedInvoiceBusinessIssuersIndexHooks = ({
  orgId,
  auth,
}: Props) => {
  const [redirectTo, setRedirectTo] = useState<redirectToType | undefined>();
  const [editTargetId, setEditTargetId] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [businessIssuers, setBusinessIssuers] = useState<
    GetInvoiceBusinessIssuersByOrgIdQuery["invoiceBusinessIssuersByOrgID"]
  >([]);

  const handleOnSetRedirectToNew = useCallback(() => {
    setRedirectTo("new");
  }, []);

  const handleOnSetRedirectToEdit = (id: string) => {
    setEditTargetId(id);
    setRedirectTo("edit");
  };

  const handleOnFetchBusinessIssuers = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await runGqlQueryV2(() =>
        gqlClient.getInvoiceBusinessIssuersByOrgId(
          { orgId: orgId! },
          gqlHeader("q"),
        ),
      );
      if (res.error) {
        throw res.error;
      }
      if (res.data?.invoiceBusinessIssuersByOrgID) {
        setBusinessIssuers(res.data.invoiceBusinessIssuersByOrgID);
      }
    } catch (e) {
      toast.error(
        "適格請求書発行事業者の取得に失敗しました。時間を置いて再度お試しください。",
      );
      reportUnexpectedError(e);
    } finally {
      setIsLoading(false);
    }
  }, [orgId]);

  const { enablePayment } = useEnablePaymentHooks({
    orgId,
  });

  useEffect(() => {
    if (auth.hasPermissions("invoice_business_issuers.read")) {
      handleOnFetchBusinessIssuers();
    }
  }, [handleOnFetchBusinessIssuers, auth]);

  return {
    enablePayment,
    isLoading,
    redirectTo,
    editTargetId,
    businessIssuers,
    handleOnSetRedirectToNew,
    handleOnSetRedirectToEdit,
  };
};
